// ============================================================================
// NUMERAÇÃO DOS ANEXOS — função única (fonte da verdade do número)
// ARQUIVO: src/utils/numeracaoAnexos.js
//
// Decisão do titular (24/09/2026): o número do anexo é decidido SÓ no Portal.
// O Word é apenas um espelho — recebe o rótulo pronto ("ANEXO 007") e imprime.
// Antes desta função, a fórmula `pos + 1` estava espalhada em ~20 pontos, com
// três regras diferentes no lado do Word. Ver PROMPT_TCREI_NUMERACAO_INICIAL_
// ANEXOS_20260924.md (fora do repositório).
//
// Regras:
//  - número = posição do slot (base 0) + `inicio` (padrão 1). Slot vazio conta.
//  - largura: 2 dígitos até o 99; 3 dígitos quando o ÚLTIMO número que existe na
//    lista da peça chega a 100 (nos dois sentidos). Todos os rótulos da peça
//    têm a mesma largura.
//  - teto: o sistema só reconhece números de até 3 dígitos (999).
// Funções puras: sem React, sem Firebase, sem DOM (testáveis com `node --test`).
// ============================================================================

export const INICIO_PADRAO = 1;
export const LARGURA_MINIMA = 2;
export const LARGURA_MAXIMA = 3;
export const LIMITE_NUMERO = 999;
const NUMERO_QUE_VIRA_LARGURA_3 = 100;

const inteiroPositivo = (v) => {
  const n = typeof v === "string" && v.trim() !== "" ? Number(v) : v;
  return typeof n === "number" && Number.isInteger(n) && n >= 1 ? n : null;
};

/**
 * Valida o "começar em". Devolve { ok, valor, motivo }.
 * `totalSlots` = tamanho ATUAL da lista da peça (checklist + extras).
 */
export function validarInicio(valor, totalSlots = 0) {
  const n = inteiroPositivo(valor);
  if (n === null) {
    return { ok: false, valor: INICIO_PADRAO, motivo: "Use um número inteiro a partir de 1." };
  }
  const total = Math.max(Number(totalSlots) || 0, 1);
  if (n + total - 1 > LIMITE_NUMERO) {
    const maximo = LIMITE_NUMERO - total + 1;
    return {
      ok: false,
      valor: INICIO_PADRAO,
      motivo: `Com ${total} ${total === 1 ? "posição" : "posições"} nesta peça, o maior início possível é ${Math.max(maximo, 1)} (o sistema só reconhece números até ${LIMITE_NUMERO}).`,
    };
  }
  return { ok: true, valor: n, motivo: "" };
}

/** Versão silenciosa para ler dado salvo: qualquer lixo vira 1. */
export function normalizarInicio(valor, totalSlots = 0) {
  return validarInicio(valor, totalSlots).valor;
}

/** Número do anexo na posição `pos` (base 0). Com início 1: pos + 1, igual a sempre. */
export function numeroDoAnexo(pos, inicio = INICIO_PADRAO) {
  const p = Number(pos);
  const base = inteiroPositivo(inicio) ?? INICIO_PADRAO;
  return (Number.isFinite(p) && p >= 0 ? Math.floor(p) : 0) + base;
}

/** Último número que EXISTE na lista da peça (preenchido ou não). */
export function ultimoNumeroDaLista(totalSlots, inicio = INICIO_PADRAO) {
  const total = Math.max(Number(totalSlots) || 0, 1);
  return numeroDoAnexo(total - 1, inicio);
}

/** 2 dígitos até o 99; 3 quando o último número da lista chega a 100. Bidirecional. */
export function larguraDoNumero(ultimoNumero) {
  return Number(ultimoNumero) >= NUMERO_QUE_VIRA_LARGURA_3 ? LARGURA_MAXIMA : LARGURA_MINIMA;
}

/** Largura vigente de uma peça, dado o tamanho da lista e o início. */
export function larguraDaPeca(totalSlots, inicio = INICIO_PADRAO) {
  return larguraDoNumero(ultimoNumeroDaLista(totalSlots, inicio));
}

export function formatarNumero(numero, largura = LARGURA_MINIMA) {
  return String(Math.max(Number(numero) || 0, 0)).padStart(largura, "0");
}

/** "ANEXO 07" / "ANEXO 007" — a mesma escrita em toda parte (tela, Drive, ZIP, Word). */
export function rotuloAnexo(numero, largura = LARGURA_MINIMA) {
  return `ANEXO ${formatarNumero(numero, largura)}`;
}

/** Rótulo do slot na posição `pos`, com a largura vigente da peça. */
export function rotuloDoSlot(pos, totalSlots, inicio = INICIO_PADRAO) {
  return rotuloAnexo(numeroDoAnexo(pos, inicio), larguraDaPeca(totalSlots, inicio));
}

/**
 * Mapa { slotId: "ANEXO 007" } para TODOS os slots da peça (vazios incluídos).
 * É o que a ponte envia ao Word: o Word só imprime.
 */
export function rotulosPorSlot(slotOrder, inicio = INICIO_PADRAO) {
  const ordem = Array.isArray(slotOrder) ? slotOrder.map(String) : [];
  const largura = larguraDaPeca(ordem.length, inicio);
  const out = {};
  ordem.forEach((slotId, pos) => {
    out[slotId] = rotuloAnexo(numeroDoAnexo(pos, inicio), largura);
  });
  return out;
}

/**
 * Número escrito no NOME de um arquivo ("ANEXO_20_X.pdf", "ANEXO 007 X.pdf",
 * "anexo-20_..."). Mesma regra do autoFill de antes; aceita zeros à esquerda.
 */
export function numeroNoNome(nome) {
  const m = String(nome || "").match(/^anexo[_\s-]*0*(\d{1,3})[_\s-]/i);
  return m ? parseInt(m[1], 10) : null;
}

/**
 * Quantos dígitos o nome do arquivo usa hoje no prefixo ("ANEXO 07 X.pdf" → 2,
 * "ANEXO 007 X.pdf" → 3). null se o nome não tem prefixo numérico reconhecível.
 * Serve para avisar "N arquivos serão renomeados" quando a largura da peça vira.
 */
export function larguraNoNome(nome) {
  const m = String(nome || "").match(/^anexo[_\s-]*(\d{1,3})[_\s-]/i);
  return m ? m[1].length : null;
}

/**
 * O Word é ESPELHO: imprime o rótulo que o Portal mandou pela ponte. Se o Portal for de uma versão
 * antiga e não mandou (`rotulo` ausente/vazio), cai no comportamento de antes ("ANEXO " + posição
 * base 1 com 2 dígitos) — nunca quebra. `indiceBase0` é a posição do anexo na lista que o Word tem.
 */
export function rotuloOuAntigo(rotulo, indiceBase0 = 0) {
  const pronto = typeof rotulo === "string" ? rotulo.trim() : "";
  if (pronto) return pronto;
  const i = Number(indiceBase0);
  return rotuloAnexo((Number.isFinite(i) && i >= 0 ? Math.floor(i) : 0) + 1, LARGURA_MINIMA);
}

/** Texto da citação no corpo da peça: "(vide ANEXO 007 - DESCRIÇÃO)". */
export function textoCitacao(rotulo, descricao) {
  return `(vide ${rotulo} - ${descricao})`;
}

/**
 * Sugestão do botão "Continuar de outra peça":
 * `inicioDaOutra` + posição (base 0) do último anexo PREENCHIDO + 1.
 * Sem nenhum anexo preenchido (`ultimaPosicaoPreenchida < 0`), continua no próprio início.
 * Slots vazios no FIM da lista não contam; slot vazio no MEIO consome número (regra do Portal).
 */
export function proximoNumeroDepoisDe(inicioDaOutra, ultimaPosicaoPreenchida) {
  const inicio = inteiroPositivo(inicioDaOutra) ?? INICIO_PADRAO;
  const ultima = Number(ultimaPosicaoPreenchida);
  if (!Number.isFinite(ultima) || ultima < 0) return inicio;
  return inicio + Math.floor(ultima) + 1;
}

/** Posição (base 0) do último slot com arquivo vinculado; -1 se nenhum. */
export function ultimaPosicaoPreenchida(slotOrder, assignments) {
  const ordem = Array.isArray(slotOrder) ? slotOrder : [];
  const atrib = assignments && typeof assignments === "object" ? assignments : {};
  let ultima = -1;
  ordem.forEach((slotId, pos) => {
    const v = atrib[slotId];
    const id = typeof v === "string" ? v.trim() : String(v?.id || "").trim();
    if (id) ultima = pos;
  });
  return ultima;
}
